import { FormattedMessage, useIntl } from 'react-intl';
import { IconButton } from '@mui/material';
import { DeleteOutlineIcon } from 'components/Icons';
import ReactHookFormField from 'components/ReactHookFormField';
import useDevelopmentNeedsField from 'hooks/pdp/form/useDevelopmentNeedsField';
import useRelatedAreaOptions from 'hooks/pdp/useRelatedAreaOptions';
import { useFormContext } from 'react-hook-form';
import { pdpTexts } from 'i18n';
import { useMemo } from 'react';
import { RelatedArea } from 'constants/pdp';
import { SimpleEmptyState } from '../SimpleEmptyState';
import messages from '../messages';
import { FieldsRow, FieldContainer } from './EditPDP.styled';
import {
  RelatedAreaField,
  DeadlineField,
  ActionStepContainer,
  CheckboxContainer,
  ActionStepButton,
  ActionStepsSectionTitle,
} from './DevelopmentNeedForm.styled';

interface DevelopmentNeedFormProps {
  needIndex: number;
}

export default function DevelopmentNeedForm({
  needIndex,
}: DevelopmentNeedFormProps) {
  const { formatMessage } = useIntl();
  const { addActionStep, removeActionStep } = useDevelopmentNeedsField(
    useFormContext(),
  );
  const { watch } = useFormContext();
  const relatedAreaOptions = useRelatedAreaOptions();
  const need = watch(`developmentNeeds.${needIndex}`) || {
    name: '',
    relatedArea: RelatedArea.EXPERTISE,
    deadline: '',
    actionSteps: [],
    requiredTrainings: [],
  };

  const relatedAreaSelectOptions = useMemo(
    () =>
      relatedAreaOptions.map((option) => ({
        id: option.id,
        name: option.name,
      })),
    [relatedAreaOptions],
  );

  return (
    <>
      <FieldContainer>
        <ReactHookFormField
          name={`developmentNeeds.${needIndex}.name`}
          type="text"
          label={<FormattedMessage {...pdpTexts.developmentNeedName} />}
          placeholder={formatMessage(messages.enterDevelopmentNeedName)}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldsRow>
          <RelatedAreaField>
            <ReactHookFormField
              name={`developmentNeeds.${needIndex}.relatedArea`}
              type="select"
              label={<FormattedMessage {...pdpTexts.relatedArea} />}
              options={relatedAreaSelectOptions}
            />
          </RelatedAreaField>
          <DeadlineField>
            <ReactHookFormField
              name={`developmentNeeds.${needIndex}.deadline`}
              type="datepicker"
              label={<FormattedMessage {...pdpTexts.deadline} />}
            />
          </DeadlineField>
        </FieldsRow>
      </FieldContainer>

      <FieldContainer noMarginBottom>
        <ActionStepsSectionTitle variant="h6">
          <FormattedMessage {...pdpTexts.actionSteps} />
        </ActionStepsSectionTitle>

        {need.actionSteps?.length === 0 ? (
          <SimpleEmptyState
            title={formatMessage(messages.noActionStepsTitle)}
            description={formatMessage(messages.noActionStepsDescription)}
          />
        ) : (
          need.actionSteps?.map((step: any, stepIndex: number) => (
            <ActionStepContainer
              // eslint-disable-next-line react/no-array-index-key
              key={`action-step-${needIndex}-${stepIndex}`}
              noMarginBottom={stepIndex === (need.actionSteps?.length || 0) - 1}
            >
              <ReactHookFormField
                name={`developmentNeeds.${needIndex}.actionSteps.${stepIndex}.name`}
                type="text"
                placeholder={formatMessage(messages.enterActionStep)}
                fieldProps={{
                  sx: { width: '535px' },
                }}
              />
              <CheckboxContainer>
                <ReactHookFormField
                  name={`developmentNeeds.${needIndex}.actionSteps.${stepIndex}.isAchieved`}
                  type="checkbox"
                  label={<FormattedMessage {...pdpTexts.isAchieved} />}
                />
              </CheckboxContainer>
              <IconButton
                size="small"
                color="primary"
                onClick={() => removeActionStep(needIndex, stepIndex)}
                aria-label={formatMessage(messages.deleteActionStep)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </ActionStepContainer>
          ))
        )}

        <ActionStepButton
          variant="outlined"
          size="small"
          onClick={() => addActionStep(needIndex)}
        >
          <FormattedMessage {...pdpTexts.addActionStep} />
        </ActionStepButton>
      </FieldContainer>
    </>
  );
}
